// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://src` | (string & {});
};

export type ChannelList = Array<'email' | 'sms' | 'in_app'>;

/**
 * Each key corresponds to a channel in the notification system. Not all channels
 * are available for every class.
 *
 */
export type ChannelMap = {
    /**
     * Subscribed (true), unsubscribed (false), or use default (null).
     */
    email?: boolean | null;
    /**
     * Subscribed (true), unsubscribed (false), or use default (null).
     */
    sms?: boolean | null;
    /**
     * Subscribed (true), unsubscribed (false), or use default (null).
     */
    in_app?: boolean | null;
};

export type ClassChannelInput = ChannelMap | boolean;

/**
 * Metadata for a taxonomy entry
 */
export type ClassMetadata = {
    name: string;
    name_message_id: string;
    icon?: string;
};

/**
 * A node in the notification class hierarchy. Leaf nodes have `channels`; intermediate
 * nodes have `classes`. A node may have both when it has its own templates and sub-classes.
 *
 */
export type Taxon = {
    channels?: ChannelList;
    forced_channels?: ChannelList;
    /**
     * List of channels that are on by default when the recipient has no explicit preference
     */
    default_channels?: ChannelList;
    metadata?: ClassMetadata;
    classes?: {
        [key: string]: Taxon;
    };
};

export type Taxonomy = {
    /**
     * List of available channels in the notification system
     */
    channels: ChannelList;
    /**
     * Hierarchical list of available classes in the notification system.
     * Each leaf node has a list of class-specific available channels.
     *
     */
    classes: {
        [key: string]: Taxon;
    };
};

/**
 * Subscription state as returned by GET and as the response to PATCH.
 */
export type Subscriptions = {
    /**
     * Global channel subscriptions applied across all classes
     */
    channels: ChannelMap;
    /**
     * Each key corresponds to a notification class (leaf classes only).
     *
     */
    classes: {
        [key: string]: {
            channels: ChannelMap;
        };
    };
};

/**
 * Subscription update sent in a PATCH request. Class-level `channels` additionally accepts
 * a boolean shorthand: `true` subscribes to all available channels for the class, `false`
 * unsubscribes from all. Forced channels are ignored silently. Null values are ignored.
 *
 */
export type SubscriptionsInput = {
    /**
     * Global channel subscriptions applied across all classes
     */
    channels: ChannelMap;
    /**
     * Each key corresponds to a notification class.
     *
     */
    classes: {
        [key: string]: {
            channels: ClassChannelInput;
        };
    };
};

/**
 * Filter results to a single application. When omitted, all applications are included.
 *
 */
export type App = string;

/**
 * Controls which entity preferences are read from or written to.
 * - `user` — per-user-profile preferences (requires the token to contain a profile)
 * - `identity` — per-identity preferences
 *
 * When omitted, defaults to `user` if the token contains a profile, otherwise `identity`.
 * Passing `aspect=user` without a profile in the token returns `400`.
 *
 */
export enum Aspect {
    USER = 'user',
    IDENTITY = 'identity'
}

export type GetV1TaxonomyData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filter results to a single application. When omitted, all applications are included.
         *
         */
        app?: string;
        /**
         * Controls which entity preferences are read from or written to.
         * - `user` — per-user-profile preferences (requires the token to contain a profile)
         * - `identity` — per-identity preferences
         *
         * When omitted, defaults to `user` if the token contains a profile, otherwise `identity`.
         * Passing `aspect=user` without a profile in the token returns `400`.
         *
         */
        aspect?: 'user' | 'identity';
    };
    url: '/v1/taxonomy';
};

export type GetV1TaxonomyErrors = {
    /**
     * Unauthorized — missing or invalid authorization token
     */
    401: unknown;
    /**
     * Entity not found for the resolved TRN
     */
    404: unknown;
    /**
     * The taxonomy could not be read from the database
     */
    500: unknown;
};

export type GetV1TaxonomyResponses = {
    /**
     * Available notification channels and classes. Scoped to the application specified by
     * `app`, or all applications when `app` is omitted.
     *
     */
    200: Taxonomy;
};

export type GetV1TaxonomyResponse = GetV1TaxonomyResponses[keyof GetV1TaxonomyResponses];

export type GetV1SubscriptionsData = {
    body?: never;
    path?: never;
    query?: {
        /**
         * Filter results to a single application. When omitted, all applications are included.
         *
         */
        app?: string;
        /**
         * Controls which entity preferences are read from or written to.
         * - `user` — per-user-profile preferences (requires the token to contain a profile)
         * - `identity` — per-identity preferences
         *
         * When omitted, defaults to `user` if the token contains a profile, otherwise `identity`.
         * Passing `aspect=user` without a profile in the token returns `400`.
         *
         */
        aspect?: 'user' | 'identity';
    };
    url: '/v1/subscriptions';
};

export type GetV1SubscriptionsErrors = {
    /**
     * Unauthorized — missing or invalid authorization token
     */
    401: unknown;
    /**
     * Entity not found for the resolved TRN
     */
    404: unknown;
};

export type GetV1SubscriptionsResponses = {
    /**
     * Resulting notification subscriptions
     */
    200: Subscriptions;
};

export type GetV1SubscriptionsResponse = GetV1SubscriptionsResponses[keyof GetV1SubscriptionsResponses];

export type PatchV1SubscriptionsData = {
    body: SubscriptionsInput;
    path?: never;
    query?: {
        /**
         * Filter results to a single application. When omitted, all applications are included.
         *
         */
        app?: string;
        /**
         * Controls which entity preferences are read from or written to.
         * - `user` — per-user-profile preferences (requires the token to contain a profile)
         * - `identity` — per-identity preferences
         *
         * When omitted, defaults to `user` if the token contains a profile, otherwise `identity`.
         * Passing `aspect=user` without a profile in the token returns `400`.
         *
         */
        aspect?: 'user' | 'identity';
    };
    url: '/v1/subscriptions';
};

export type PatchV1SubscriptionsErrors = {
    /**
     * Bad request — invalid aspect, unknown channel or class, or schema validation failure
     */
    400: unknown;
    /**
     * Unauthorized — missing or invalid authorization token
     */
    401: unknown;
    /**
     * Entity not found for the resolved TRN
     */
    404: unknown;
};

export type PatchV1SubscriptionsResponses = {
    /**
     * Resulting notification subscriptions after the update
     */
    200: Subscriptions;
};

export type PatchV1SubscriptionsResponse = PatchV1SubscriptionsResponses[keyof PatchV1SubscriptionsResponses];
